import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getFriendsPageInfos } from "../../../functions/user";
import { createGroup } from "../../../functions/chat";
import { uploadImages } from "../../../functions/uploadImages";
import { BackIcon, SearchIcon, CameraIcon, CheckIcon } from "../../../svg/chat";
import { PulseLoader } from "react-spinners";
import "./style.css";

export default function CreateGroup() {
  const { user } = useSelector((state) => ({ ...state }));
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1); // 1: Select participants, 2: Group info
  const [friends, setFriends] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [groupPicture, setGroupPicture] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingFriends, setLoadingFriends] = useState(true);
  const [error, setError] = useState("");

  // Load friends
  useEffect(() => {
    const loadFriends = async () => {
      try {
        setLoadingFriends(true);
        const result = await getFriendsPageInfos(user.token);
        if (result.status === "ok") {
          setFriends(result.data.friends || []);
        } else {
          setError("Erro ao carregar amigos");
        }
      } catch (err) {
        console.error("Error loading friends:", err);
        setError("Erro ao carregar amigos");
      } finally {
        setLoadingFriends(false);
      }
    };

    if (user?.token) {
      loadFriends();
    }
  }, [user?.token]);

  // Filter friends based on search
  const filteredFriends = friends.filter((friend) => {
    const name = `${friend.first_name} ${friend.last_name}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  // Toggle participant selection
  const toggleParticipant = (friend) => {
    setSelectedParticipants((prev) => {
      const isSelected = prev.some((p) => p._id === friend._id);
      if (isSelected) {
        return prev.filter((p) => p._id !== friend._id);
      } else {
        return [...prev, friend];
      }
    });
  };

  // Remove selected participant
  const removeParticipant = (friendId) => {
    setSelectedParticipants((prev) => prev.filter((p) => p._id !== friendId));
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setGroupPicture(file);
      setPicturePreview(URL.createObjectURL(file));
    }
  };

  // Go to next step
  const handleNext = () => {
    if (selectedParticipants.length < 1) {
      setError("Selecione pelo menos 1 participante");
      return;
    }
    setError("");
    setStep(2);
  };

  // Go back
  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else {
      navigate("/chat");
    }
  };

  // Create group
  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setError("Digite um nome para o grupo");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let pictureUrl = null;

      // Upload picture if selected
      if (groupPicture) {
        const formData = new FormData();
        formData.append("file", groupPicture);
        formData.append("path", "socializa/groups");

        const uploadResult = await uploadImages(formData, user.token);
        if (uploadResult && uploadResult.length > 0) {
          pictureUrl = uploadResult[0].url || uploadResult[0];
        }
      }

      // Create group
      const groupData = {
        name: groupName.trim(),
        description: groupDescription.trim(),
        picture: pictureUrl,
        participants: selectedParticipants.map((p) => p._id),
      };

      await createGroup(groupData, user.token);

      // Navigate to the new group chat
      navigate(`/chat`);
    } catch (err) {
      console.error("Error creating group:", err);
      setError(err.response?.data?.message || "Erro ao criar grupo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create_group_page">
      <div className="create_group_container">
        {/* Header */}
        <div className="create_group_header">
          <button className="back_btn" onClick={handleBack}>
            <BackIcon />
          </button>
          <div className="header_info">
            <h2>{step === 1 ? "Novo grupo" : "Info do grupo"}</h2>
            {step === 1 && (
              <span className="participants_count">
                {selectedParticipants.length} de {friends.length} selecionados
              </span>
            )}
          </div>
          {step === 1 && selectedParticipants.length > 0 && (
            <button className="next_btn" onClick={handleNext}>
              <CheckIcon color="#00a884" />
            </button>
          )}
        </div>

        {/* Error message */}
        {error && <div className="error_message">{error}</div>}

        {/* Step 1: Select Participants */}
        {step === 1 && (
          <>
            {/* Selected participants preview */}
            {selectedParticipants.length > 0 && (
              <div className="selected_participants">
                {selectedParticipants.map((participant) => (
                  <div
                    key={participant._id}
                    className="selected_participant"
                    onClick={() => removeParticipant(participant._id)}
                  >
                    <div className="participant_avatar">
                      <img
                        src={participant.picture}
                        alt={participant.first_name}
                        onError={(e) => {
                          e.target.src =
                            "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                        }}
                      />
                      <span className="remove_badge">×</span>
                    </div>
                    <span className="participant_name">
                      {participant.first_name}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Search */}
            <div className="participant_search">
              <SearchIcon />
              <input
                type="text"
                placeholder="Pesquisar amigos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Friends list */}
            <div className="friends_list">
              {loadingFriends ? (
                <div className="loading_friends">
                  <PulseLoader color="#00a884" size={10} />
                </div>
              ) : filteredFriends.length === 0 ? (
                <div className="no_friends">
                  <p>Nenhum amigo encontrado</p>
                </div>
              ) : (
                filteredFriends.map((friend) => {
                  const isSelected = selectedParticipants.some(
                    (p) => p._id === friend._id
                  );
                  return (
                    <div
                      key={friend._id}
                      className={`friend_item ${isSelected ? "selected" : ""}`}
                      onClick={() => toggleParticipant(friend)}
                    >
                      <div className="friend_avatar">
                        <img
                          src={friend.picture}
                          alt={friend.first_name}
                          onError={(e) => {
                            e.target.src =
                              "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                          }}
                        />
                      </div>
                      <div className="friend_info">
                        <span className="friend_name">
                          {friend.first_name} {friend.last_name}
                        </span>
                        <span className="friend_username">@{friend.username}</span>
                      </div>
                      <div className={`checkbox ${isSelected ? "checked" : ""}`}>
                        {isSelected && <CheckIcon color="#fff" />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Step 2: Group Info */}
        {step === 2 && (
          <div className="group_info_step">
            {/* Group picture */}
            <div
              className="group_picture_upload"
              onClick={() => fileInputRef.current?.click()}
            >
              {picturePreview ? (
                <img src={picturePreview} alt="Group" />
              ) : (
                <div className="picture_placeholder">
                  <CameraIcon />
                  <span>Adicionar foto</span>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageSelect}
                hidden
              />
            </div>

            {/* Group name */}
            <div className="group_input_group">
              <input
                type="text"
                placeholder="Nome do grupo"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                maxLength={50}
              />
              <span className="char_count">{groupName.length}/50</span>
            </div>

            {/* Group description */}
            <div className="group_input_group">
              <textarea
                placeholder="Descrição do grupo (opcional)"
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                maxLength={200}
                rows={3}
              />
              <span className="char_count">{groupDescription.length}/200</span>
            </div>

            {/* Participants preview */}
            <div className="participants_preview">
              <h4>Participantes: {selectedParticipants.length}</h4>
              <div className="participants_avatars">
                {selectedParticipants.slice(0, 5).map((p) => (
                  <img
                    key={p._id}
                    src={p.picture}
                    alt={p.first_name}
                    title={`${p.first_name} ${p.last_name}`}
                    onError={(e) => {
                      e.target.src =
                        "https://res.cloudinary.com/dmhcnhtng/image/upload/v1643044376/avatars/default_pic_jeaybr.png";
                    }}
                  />
                ))}
                {selectedParticipants.length > 5 && (
                  <span className="more_count">
                    +{selectedParticipants.length - 5}
                  </span>
                )}
              </div>
            </div>

            {/* Create button */}
            <button
              className="create_group_btn"
              onClick={handleCreateGroup}
              disabled={loading || !groupName.trim()}
            >
              {loading ? (
                <PulseLoader color="#fff" size={8} />
              ) : (
                <>
                  <CheckIcon color="#fff" />
                  Criar Grupo
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
